import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {
    Autocomplete,
    Card,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    TextField as MaterialTextField,
    Typography
} from "@mui/material";
import {ArrowCircleUp, Close} from "@mui/icons-material";
import {Field, Form, Formik} from "formik";
import {TextField} from "formik-material-ui";
import Button from "@mui/material/Button";
import {LoadingButton} from "@mui/lab";
import {connect} from "react-redux";
import {actions as opportunityActions, selectors as opportunitySelectors} from "../../../Ducks/opportunity";
import {bindActionCreators} from "redux";
import {parseCVR, validateEan, validateEmail} from "../../Utils/validations";
import {createBookkeeper, updateBookkeeper} from "../../../API/bookkeeper";
import {toast} from "react-toastify";
import {getPaymentTerms} from "../../../API/invoice";
import {getCountries} from "../../../API/companies";
import {DENMARK, SWEDEN} from "../../Utils/constants";

const ManageBookkeeperModal = (props) => {
    const {open, data, cvr, searchCvrLoading} = props;
    const formRef = useRef(null);
    const [bookkeeper, setBookkeeper] = useState(null);
    const [suggestedCompanyName, setSuggestedCompanyName] = useState('');
    const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
    const [countriesOptions, setCountriesOptions] = useState([]);

    useEffect(() => {
        if (open) {
            setSuggestedCompanyName('');
        }

        setBookkeeper(data);
        loadPaymentTerms();
        loadCountries();
    }, [data, open]);

    useEffect(() => {
        formRef.current?.setFieldValue("cvr", bookkeeper?.cvr);
        formRef.current?.setFieldValue("company_name", bookkeeper?.company_name);
        formRef.current?.setFieldValue("ean", bookkeeper?.ean);
        formRef.current?.setFieldValue("bookkeeper_email", bookkeeper?.bookkeeper_email);
        formRef.current?.setFieldValue("po_number", bookkeeper?.po_number);
        formRef.current?.setFieldValue("payment_terms", bookkeeper?.payment_terms || '');
        formRef.current?.setFieldValue("country_id", bookkeeper?.country_id || '');
    }, [bookkeeper, formRef]);

    useEffect(() => {
        if (cvr) {
            let parsedCvr = parseCVR(cvr);

            if (parsedCvr !== false) {
                if (data) {
                    setSuggestedCompanyName(parsedCvr.company_name);
                } else {
                    formRef?.current?.setFieldValue('company_name', parsedCvr.company_name);
                }
            } else {
                setSuggestedCompanyName('');
            }
        }
    }, [data, cvr, searchCvrLoading]);

    const loadPaymentTerms = () => {
        getPaymentTerms()
            .then((res) => {
                if (res.data) {
                    setPaymentTermsOptions(res.data);
                }
            })
            .catch((error) => {
                toast.error(error);
            });
    }

    const loadCountries = () => {
        getCountries().then((res) => {
            setCountriesOptions(res.data.main?.filter((country) => country.country_name === DENMARK || country.country_name === SWEDEN));
        });
    }

    const handleClose = () => {
        setBookkeeper(null);
        setSuggestedCompanyName('');

        props.handleClose(false);
    }

    const handleCVR = (e) => {
        const {value} = e.target;

        if (value !== "" && !value.includes("-")) {
            props.searchCvr(value);
        }
    }

    return (
        <Dialog key={open ? 'open' : 'closed'} open={open} scroll="paper" fullWidth maxWidth={'md'}
                sx={{'& .MuiDialog-paper': {maxHeight: '80vh'}}}>
            <DialogTitle
                sx={{backgroundColor: '#1565c0', color: 'white'}}>Manage Bookkeeper</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[100],
                })}
            >
                <Close/>
            </IconButton>
            <DialogContent sx={{padding: '14px', backgroundColor: '#F7F7F7'}}>
                <Grid container spacing={0}>
                    <Grid item xs={12}>
                        <Card sx={{padding: '14px', marginRight: '7px', backgroundColor: 'white'}}>
                            <Typography
                                variant="span"
                                component="span"
                                sx={{fontWeight: 'bold', marginBottom: '10px'}}
                            >
                                {bookkeeper ? 'Edit' : 'Add'} Bookkeeper
                            </Typography>
                            <Formik
                                innerRef={formRef}
                                initialValues={{
                                    cvr: '',
                                    company_name: '',
                                    ean: '',
                                    bookkeeper_email: '',
                                    po_number: '',
                                    payment_terms: '',
                                    country_id: ''
                                }}
                                validate={(values) => {
                                    const errors = {};

                                    if (!values.cvr) {
                                        errors.cvr = 'Required';
                                    }

                                    if (!values.company_name) {
                                        errors.company_name = 'Required';
                                    }

                                    // At least one of ean, bookkeeper_email, or payment_terms must be provided
                                    if (!values.ean && !values.bookkeeper_email && !values.payment_terms) {
                                        errors.ean = 'At least one of EAN, Bookkeeper Email, or Payment Terms is required';
                                        errors.bookkeeper_email = 'At least one of EAN, Bookkeeper Email, or Payment Terms is required';
                                        errors.payment_terms = 'At least one of EAN, Bookkeeper Email, or Payment Terms is required';
                                    }

                                    if (values.bookkeeper_email && !validateEmail(values.bookkeeper_email)) {
                                        errors.bookkeeper_email = 'Invalid email address';
                                    }

                                    if (values.ean) {
                                        if (values.ean.length !== 13) {
                                            errors.ean = 'EAN should be 13 characters';
                                        } else {
                                            let country = DENMARK;

                                            if (values.country_id) {
                                                country = countriesOptions.find((country) => country.country_id === values.country_id).country_name;
                                            }

                                            const eanValid = validateEan(values.ean, false, country);

                                            if (eanValid.valid === false) {
                                                errors.ean = eanValid.error;
                                            }
                                        }
                                    }

                                    return errors;
                                }}
                                onSubmit={(values, {setSubmitting}) => {
                                    if (bookkeeper) {
                                        updateBookkeeper(bookkeeper.bookkeeper_id, values)
                                            .then((res) => {
                                                if (res?.message) {
                                                    toast.success(res.message);
                                                    props.handleClose(true);
                                                }
                                                setSubmitting(false);
                                            })
                                            .catch((error) => {
                                                toast.error(error.message);
                                                setSubmitting(false);
                                            });
                                    } else {
                                        createBookkeeper(values)
                                            .then((res) => {
                                                if (res?.message) {
                                                    toast.success(res.message);
                                                    setSuggestedCompanyName('');
                                                    props.handleClose(true);
                                                }
                                                setSubmitting(false);
                                            })
                                            .catch((error) => {
                                                toast.error(error.message);
                                                setSubmitting(false);
                                            });
                                    }
                                }}
                            >
                                {({values, isSubmitting, setFieldValue, setFieldTouched, errors, submitCount}) => (
                                    <Form>
                                        <Grid container spacing={2} sx={{marginTop: '10px'}}>
                                            <Grid item xs={3} style={{margin: 'auto'}}>
                                                <Typography variant="span" component="span">
                                                    CVR*
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={9} style={{margin: 'auto'}}>
                                                <Field
                                                    name="cvr"
                                                    type="text"
                                                    label="CVR*"
                                                    variant="outlined"
                                                    margin="normal"
                                                    fullWidth={true}
                                                    component={TextField}
                                                    size="small"
                                                    onBlur={handleCVR}
                                                    InputProps={{
                                                        endAdornment: props.searchCvrLoading ? (
                                                            <InputAdornment position="end">
                                                                <CircularProgress size={20}/>
                                                            </InputAdornment>
                                                        ) : null,
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2} sx={{marginTop: '10px'}}>
                                            <Grid item xs={3} style={{margin: 'auto'}}>
                                                <Typography variant="span" component="span">
                                                    Company Name*
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={9} style={{margin: 'auto'}}>
                                                <Field
                                                    name="company_name"
                                                    type="text"
                                                    label="Company Name*"
                                                    variant="outlined"
                                                    margin="normal"
                                                    fullWidth={true}
                                                    component={TextField}
                                                    size="small"
                                                    style={{marginBottom: '0px'}}
                                                />
                                                <div style={{fontSize: '12px', color: 'gray', padding: '0px'}}>
                                                    <span>{suggestedCompanyName}</span>
                                                    {suggestedCompanyName && (
                                                        <IconButton
                                                            sx={{fontSize: '10px'}}
                                                            onClick={() => {
                                                                setFieldValue('company_name', suggestedCompanyName);
                                                            }}
                                                        >
                                                            <ArrowCircleUp color="primary"/>
                                                        </IconButton>
                                                    )}
                                                </div>
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2} sx={{marginTop: '10px'}}>
                                            <Grid item xs={3} style={{margin: 'auto'}}>
                                                <Typography variant="span" component="span">
                                                    EAN*
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={9} style={{margin: 'auto'}}>
                                                <Field
                                                    name="ean"
                                                    type="text"
                                                    label="EAN*"
                                                    variant="outlined"
                                                    margin="normal"
                                                    fullWidth={true}
                                                    component={TextField}
                                                    size="small"
                                                    onBlur={() => {
                                                        let _ean = values?.ean?.trim();
                                                        _ean = _ean?.replace(/^0+/, '');
                                                        setFieldValue('ean', _ean);
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2} sx={{marginTop: '10px'}}>
                                            <Grid item xs={3} style={{margin: 'auto'}}>
                                                <Typography variant="span" component="span">
                                                    Bookkeeper Email*
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={9} style={{margin: 'auto'}}>
                                                <Field
                                                    name="bookkeeper_email"
                                                    type="text"
                                                    label="Bookkeeper Email*"
                                                    variant="outlined"
                                                    margin="normal"
                                                    fullWidth={true}
                                                    component={TextField}
                                                    size="small"
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2} sx={{marginTop: '10px'}}>
                                            <Grid item xs={3} style={{margin: 'auto'}}>
                                                <Typography variant="span" component="span">
                                                    PO Number
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={9} style={{margin: 'auto'}}>
                                                <Field
                                                    name="po_number"
                                                    label="PO Number"
                                                    variant="outlined"
                                                    margin="normal"
                                                    fullWidth={true}
                                                    component={TextField}
                                                    size="small"
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2} sx={{marginTop: '10px'}}>
                                            <Grid item xs={3} style={{margin: 'auto'}}>
                                                <Typography variant="span" component="span">
                                                    Payment Terms*
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={9} style={{margin: 'auto'}}>
                                                <Autocomplete
                                                    name="payment_terms"
                                                    disablePortal
                                                    margin="normal"
                                                    size="small"
                                                    id="combo-box"
                                                    options={paymentTermsOptions}
                                                    value={paymentTermsOptions.find((option) => option.name === values.payment_terms) || null}
                                                    getOptionLabel={(option) => option.name}
                                                    isOptionEqualToValue={(option, value) => option.name === value?.name}
                                                    onChange={(_event, newValue) => {
                                                        const selected = newValue ? newValue.name : '';
                                                        setFieldValue('payment_terms', selected);
                                                    }}
                                                    onBlur={() => {
                                                        setFieldTouched('payment_terms', true);
                                                    }}
                                                    renderInput={(params) => (
                                                        <MaterialTextField
                                                            {...params}
                                                            label="Payment Terms*"
                                                            error={submitCount > 0 && !!errors.payment_terms}
                                                            helperText={submitCount > 0 && errors.payment_terms}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2} sx={{marginTop: '10px'}}>
                                            <Grid item xs={3} style={{margin: 'auto'}}>
                                                <Typography variant="span" component="span">
                                                    Country
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={9} style={{margin: 'auto'}}>
                                                <Autocomplete
                                                    name="country_id"
                                                    disablePortal
                                                    margin="normal"
                                                    size="small"
                                                    id="combo-box"
                                                    options={countriesOptions}
                                                    value={countriesOptions.find((option) => option.country_id === values.country_id) || null}
                                                    getOptionLabel={(option) => option.country_name + ' (' + option.country_code + ')'}
                                                    isOptionEqualToValue={(option, value) => option.country_id === value?.country_id}
                                                    onChange={(_event, newValue) => {
                                                        const selected = newValue ? newValue.country_id : '';
                                                        setFieldValue('country_id', selected);
                                                    }}
                                                    onBlur={() => {
                                                        setFieldTouched('country_id', true);
                                                    }}
                                                    renderInput={(params) => (
                                                        <MaterialTextField
                                                            {...params}
                                                            label="Country"
                                                            inputProps={{
                                                                ...params.inputProps,
                                                                readOnly: true, // to turn off autocomplete suggestions by browser
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container sx={{marginBottom: '15px'}} spacing={1}>
                                            <Grid item xs={12} sx={{display: 'flex', justifyContent: 'right'}}>
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    onClick={() => {
                                                        handleClose();
                                                    }}
                                                    style={{margin: '10px', width: '85px'}}
                                                    disabled={isSubmitting}
                                                >
                                                    Cancel
                                                </Button>
                                                <LoadingButton
                                                    variant="contained"
                                                    color="primary"
                                                    type="submit"
                                                    style={{margin: '10px', width: '85px'}}
                                                    loading={isSubmitting}
                                                >
                                                    {bookkeeper ? 'Update' : 'Save'}
                                                </LoadingButton>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                )}
                            </Formik>
                        </Card>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

const mapStateToProps = (state) => ({
    cvr: opportunitySelectors.cvr(state),
    searchCvrLoading: opportunitySelectors.searchCvrLoading(state)
});

const mapDispatchToProps = (dispatch) => bindActionCreators(
    {
        searchCvr: opportunityActions.searchCvr,
    },
    dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ManageBookkeeperModal);